import type { CSSProperties } from 'react';

import type { SizesString } from '@/app/components/media/app-image/shared/image-size-types';
import type {
  SpartanProductHeroBannerBoxLayout,
  SpartanProductHeroBannerLogoResponsiveLayout,
  SpartanProductHeroBannerLogoSize,
  SpartanProductHeroBannerParallaxCarMedia,
  SpartanProductHeroBannerParallaxLayerMedia,
  SpartanProductHeroBannerSceneLayout,
  SpartanProductHeroBannerSpecialLogoScene,
} from '@/app/lcv/components/sections/spartan/products/hero-banner/types';

type SpartanProductHeroBannerLayoutBreakpoint =
  keyof SpartanProductHeroBannerSceneLayout;

type SpartanProductHeroBannerParallaxBreakpoint = 'mobile' | 'desktop';

type SpartanProductHeroBannerLogoLayoutBreakpoint =
  keyof SpartanProductHeroBannerLogoResponsiveLayout;

const CSS_VAR_PREFIX = 'spartan-hero';
const HERO_LOGO_FLUID_START_VIEWPORT_WIDTH = 360;
const HERO_LOGO_FLUID_END_VIEWPORT_WIDTH = 1024;

const formatCssNumber = (value: number) => Number(value.toFixed(6)).toString();

const toPercent = (value: number, ref: number) => `${(value / ref) * 100}%`;

const toAspectRatioVar = (width: number, height: number) =>
  `${width}/${height}`;

const toFluidViewportLength = (start: number, end: number) => {
  const viewportRange =
    HERO_LOGO_FLUID_END_VIEWPORT_WIDTH - HERO_LOGO_FLUID_START_VIEWPORT_WIDTH;
  const viewportCoefficient = ((end - start) / viewportRange) * 100;
  const offset =
    start - (viewportCoefficient / 100) * HERO_LOGO_FLUID_START_VIEWPORT_WIDTH;
  const operator = offset < 0 ? '-' : '+';

  return `calc(${formatCssNumber(viewportCoefficient)}vw ${operator} ${formatCssNumber(Math.abs(offset))}px)`;
};

const getContainerWidth = (box: SpartanProductHeroBannerBoxLayout) =>
  box.containerWidth ?? box.width;

const getContainerHeight = (box: SpartanProductHeroBannerBoxLayout) =>
  box.containerHeight ?? box.height;

const sceneContainerBoxToVars = (
  box: SpartanProductHeroBannerBoxLayout,
  suffix: SpartanProductHeroBannerLayoutBreakpoint
) => ({
  [`--${CSS_VAR_PREFIX}-scene-${suffix}-frame-max-w`]: `${getContainerWidth(
    box
  )}px`,
  [`--${CSS_VAR_PREFIX}-scene-${suffix}-frame-ar`]: toAspectRatioVar(
    getContainerWidth(box),
    getContainerHeight(box)
  ),
  [`--${CSS_VAR_PREFIX}-scene-${suffix}-w`]: toPercent(
    box.width,
    getContainerWidth(box)
  ),
  [`--${CSS_VAR_PREFIX}-scene-${suffix}-max-w`]: `${box.width}px`,
  [`--${CSS_VAR_PREFIX}-scene-${suffix}-max-h`]: `${box.height}px`,
  [`--${CSS_VAR_PREFIX}-scene-${suffix}-ar`]: toAspectRatioVar(
    box.width,
    box.height
  ),
});

const sceneElementBoxToVars =
  (prefix: string) =>
  (
    box: SpartanProductHeroBannerBoxLayout,
    sceneBox: SpartanProductHeroBannerBoxLayout,
    suffix:
      | SpartanProductHeroBannerLayoutBreakpoint
      | SpartanProductHeroBannerParallaxBreakpoint
  ) => ({
    [`--${CSS_VAR_PREFIX}-${prefix}-${suffix}-w`]: toPercent(
      box.width,
      sceneBox.width
    ),
    [`--${CSS_VAR_PREFIX}-${prefix}-${suffix}-ar`]: toAspectRatioVar(
      box.width,
      box.height
    ),
    ...(box.left != null
      ? {
          [`--${CSS_VAR_PREFIX}-${prefix}-${suffix}-left`]: toPercent(
            box.left,
            sceneBox.width
          ),
        }
      : {}),
    ...(box.right != null
      ? {
          [`--${CSS_VAR_PREFIX}-${prefix}-${suffix}-right`]: toPercent(
            box.right,
            sceneBox.width
          ),
        }
      : {}),
    ...(box.bottom != null
      ? {
          [`--${CSS_VAR_PREFIX}-${prefix}-${suffix}-bottom`]: toPercent(
            box.bottom,
            sceneBox.height
          ),
        }
      : {}),
  });

const responsiveBoxToVars = (
  layout: SpartanProductHeroBannerSceneLayout,
  toVars: (
    box: SpartanProductHeroBannerBoxLayout,
    suffix: SpartanProductHeroBannerLayoutBreakpoint
  ) => Record<string, string>
) =>
  ({
    ...toVars(layout.mobile, 'mobile'),
    ...toVars(layout.desktop, 'desktop'),
    ...toVars(layout.wide, 'wide'),
  }) as CSSProperties;

const heroLogoBoxToVars =
  (prefix: string) =>
  (
    box: SpartanProductHeroBannerLogoSize,
    suffix: SpartanProductHeroBannerLogoLayoutBreakpoint
  ) => ({
    [`--${CSS_VAR_PREFIX}-${prefix}-${suffix}-w`]: `${box.width}px`,
    [`--${CSS_VAR_PREFIX}-${prefix}-${suffix}-h`]: `${box.height}px`,
    [`--${CSS_VAR_PREFIX}-${prefix}-${suffix}-ar`]: toAspectRatioVar(
      box.width,
      box.height
    ),
  });

const heroLogoResponsiveBoxToVars = (
  layout: SpartanProductHeroBannerLogoResponsiveLayout,
  prefix: string
) =>
  ({
    ...heroLogoBoxToVars(prefix)(layout.mobile, 'mobile'),
    ...heroLogoBoxToVars(prefix)(layout.lg, 'lg'),
    [`--${CSS_VAR_PREFIX}-${prefix}-fluid-w`]: toFluidViewportLength(
      layout.mobile.width,
      layout.lg.width
    ),
    [`--${CSS_VAR_PREFIX}-${prefix}-fluid-h`]: toFluidViewportLength(
      layout.mobile.height,
      layout.lg.height
    ),
  }) as CSSProperties;

export const getSpartanProductHeroBannerSceneLayoutStyle = (
  scene: SpartanProductHeroBannerSceneLayout
): CSSProperties => responsiveBoxToVars(scene, sceneContainerBoxToVars);

export const getSpartanProductHeroBannerParallaxLayerLayoutStyle = (
  media: SpartanProductHeroBannerParallaxLayerMedia,
  scene: SpartanProductHeroBannerSceneLayout,
  prefix: string
): CSSProperties =>
  ({
    ...sceneElementBoxToVars(prefix)(media.mobile, scene.mobile, 'mobile'),
    ...sceneElementBoxToVars(prefix)(media.desktop, scene.desktop, 'desktop'),
  }) as CSSProperties;

const carBoxToVars = (
  box: SpartanProductHeroBannerBoxLayout,
  sceneBox: SpartanProductHeroBannerBoxLayout,
  suffix: SpartanProductHeroBannerParallaxBreakpoint
) => ({
  [`--${CSS_VAR_PREFIX}-car-${suffix}-w`]: toPercent(box.width, sceneBox.width),
  [`--${CSS_VAR_PREFIX}-car-${suffix}-ar`]: toAspectRatioVar(
    box.width,
    box.height
  ),
  ...(box.left != null
    ? {
        [`--${CSS_VAR_PREFIX}-car-${suffix}-left`]: toPercent(
          box.left,
          sceneBox.width
        ),
      }
    : {}),
  ...(box.bottom != null
    ? {
        [`--${CSS_VAR_PREFIX}-car-${suffix}-bottom`]: toPercent(
          box.bottom,
          sceneBox.height
        ),
      }
    : {}),
});

/** Car layout is positioned relative to scene width/height (no car-level ref*). */
export const getSpartanProductHeroBannerCarLayoutStyle = (
  media: SpartanProductHeroBannerParallaxCarMedia,
  scene: SpartanProductHeroBannerSceneLayout
): CSSProperties =>
  ({
    ...carBoxToVars(media.mobile, scene.mobile, 'mobile'),
    ...carBoxToVars(media.desktop, scene.desktop, 'desktop'),
  }) as CSSProperties;

export const getSpartanProductHeroBannerModelLogoLayoutStyle = (container: {
  layout: SpartanProductHeroBannerLogoResponsiveLayout;
}): CSSProperties =>
  heroLogoResponsiveBoxToVars(container.layout, 'model-logo');

const getSpartanProductHeroBannerLogoSizes = (
  layout: SpartanProductHeroBannerLogoResponsiveLayout
): SizesString =>
  [
    `(min-width: ${HERO_LOGO_FLUID_END_VIEWPORT_WIDTH}px) ${layout.lg.width}px`,
    `(min-width: ${HERO_LOGO_FLUID_START_VIEWPORT_WIDTH}px) ${toFluidViewportLength(layout.mobile.width, layout.lg.width)}`,
    'calc(100vw - 32px)',
  ].join(', ') as SizesString;

export const getSpartanProductHeroBannerModelLogoSizes =
  getSpartanProductHeroBannerLogoSizes;

export const getSpartanProductHeroBannerEngineLogoLayoutStyle = (container: {
  layout: SpartanProductHeroBannerLogoResponsiveLayout;
}): CSSProperties =>
  heroLogoResponsiveBoxToVars(container.layout, 'engine-logo');

export const getSpartanProductHeroBannerEngineLogoSizes =
  getSpartanProductHeroBannerLogoSizes;

/** Scene-anchored special logo with independent mobile + desktop boxes. */
export const getSpartanProductHeroBannerSpecialLogoSceneLayoutStyle = (
  scene: Pick<SpartanProductHeroBannerSceneLayout, 'mobile' | 'desktop'>,
  logoScene: SpartanProductHeroBannerSpecialLogoScene
): CSSProperties =>
  ({
    ...sceneElementBoxToVars('special-logo-scene')(
      logoScene.mobile,
      scene.mobile,
      'mobile'
    ),
    ...sceneElementBoxToVars('special-logo-scene')(
      logoScene.desktop,
      scene.desktop,
      'desktop'
    ),
  }) as CSSProperties;

export const getSpartanProductHeroBannerMediaImageSize = (layout: {
  desktop: Pick<SpartanProductHeroBannerBoxLayout, 'width' | 'height'>;
}) => ({
  width: layout.desktop.width,
  height: layout.desktop.height,
});

/**
 * Width of the rendered scene in layout.module.css, scaled to this media box.
 * The CSS height guard uses max(), which Safari 16 ignores inside sizes.
 */
export const getSpartanProductHeroBannerParallaxMediaSizes = (
  media: {
    mobile: Pick<SpartanProductHeroBannerBoxLayout, 'width'>;
    desktop: Pick<SpartanProductHeroBannerBoxLayout, 'width'>;
  },
  scene: SpartanProductHeroBannerSceneLayout
): SizesString => {
  const mobileSceneRatio = media.mobile.width / scene.mobile.width;
  const desktopFrameWidth = getContainerWidth(scene.desktop);
  const desktopViewportRatio = media.desktop.width / desktopFrameWidth;
  const wideViewportRatio = media.desktop.width / scene.desktop.width;

  return [
    `(min-width: 1680px) ${formatCssNumber(wideViewportRatio * 100)}vw`,
    `(min-width: ${desktopFrameWidth}px) ${formatCssNumber(media.desktop.width)}px`,
    `(min-width: 1033px) ${formatCssNumber(desktopViewportRatio * 100)}vw`,
    `(orientation: landscape) ${formatCssNumber(desktopViewportRatio * 100)}vw`,
    `(min-width: 768px) ${formatCssNumber(media.mobile.width)}px`,
    `(min-width: 360px) calc(${formatCssNumber(70.588235 * mobileSceneRatio)}vw + ${formatCssNumber(225.882353 * mobileSceneRatio)}px)`,
    `${formatCssNumber(133.333333 * mobileSceneRatio)}vw`,
  ].join(', ') as SizesString;
};

export const getSpartanProductHeroBannerSpecialLogoSizes = (
  scene: SpartanProductHeroBannerSceneLayout,
  logoScene: SpartanProductHeroBannerSpecialLogoScene
): SizesString =>
  getSpartanProductHeroBannerParallaxMediaSizes(logoScene, scene);
